import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, 
  Mail, 
  Heart, 
  Settings, 
  Lock, 
  LogOut,
  ChevronRight,
  Save,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

export const Profile = () => {
  const { user, userProfile, logout, updateUserProfile, updatePassword } = useAuth();
  const navigate = useNavigate();
  
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-navy mb-4">
            Bitte melde dich an
          </h2>
          <Link to="/login">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Zur Anmeldung
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateUserProfile({ displayName });
      toast.success('Profil aktualisiert!');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Aktualisierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    setLoading(true);
    
    try {
      await updatePassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Passwort aktualisiert!');
    } catch (error) {
      console.error('Password update error:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Bitte melde dich erneut an, um das Passwort zu ändern');
      } else {
        toast.error('Passwortänderung fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-2">
            Mein Profil
          </h1>
          <p className="text-olive">Verwalte dein Konto und Einstellungen</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-sm p-6"
            >
              {/* User Info */}
              <div className="text-center mb-6 pb-6 border-b border-gray-100">
                <div className="w-20 h-20 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {userProfile?.photoURL ? (
                    <img 
                      src={userProfile.photoURL} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10 text-terracotta" />
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold text-navy">
                  {userProfile?.displayName || 'Benutzer'}
                </h3>
                <p className="text-olive text-sm">{user.email}</p>
                {userProfile?.isAdmin && (
                  <span className="inline-block mt-2 px-3 py-1 bg-sage/20 text-sage text-xs font-semibold rounded-full">
                    Administrator
                  </span>
                )}
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    activeTab === 'profile' ? 'bg-terracotta/10 text-terracotta' : 'text-navy hover:bg-gray-50'
                  }`}
                  data-testid="profile-tab"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Persönliche Daten</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('security')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    activeTab === 'security' ? 'bg-terracotta/10 text-terracotta' : 'text-navy hover:bg-gray-50'
                  }`}
                  data-testid="security-tab"
                >
                  <Lock className="w-5 h-5" />
                  <span className="font-medium">Sicherheit</span>
                </button>
                
                <Link
                  to="/favoriten"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-navy hover:bg-gray-50 transition-colors"
                  data-testid="favorites-link"
                >
                  <Heart className="w-5 h-5" />
                  <span className="font-medium">Favoriten</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Link>
                
                {userProfile?.isAdmin ? (
                  <Link
                    to="/admin"
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-navy hover:bg-gray-50 transition-colors"
                    data-testid="admin-link"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="font-medium">Admin</span>
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Link>
                ) : (
                  <Link
                    to="/setup-admin"
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-terracotta hover:bg-terracotta/10 transition-colors"
                    data-testid="become-admin-link"
                  >
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">Werde jetzt Admin</span>
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Link>
                )}
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Abmelden</span>
                </button>
              </nav>
            </motion.div>
          </div>

          {/* Content */}
          <div className="md:col-span-2">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl shadow-sm p-6 sm:p-8"
            >
              {activeTab === 'profile' && (
                <>
                  <h2 className="font-display text-xl font-semibold text-navy mb-6">
                    Persönliche Daten
                  </h2>
                  
                  <form onSubmit={handleUpdateProfile} className="space-y-5">
                    <div>
                      <Label htmlFor="email" className="text-navy">E-Mail</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                        <Input
                          id="email"
                          type="email"
                          value={user.email}
                          disabled
                          className="pl-10 py-5 rounded-xl bg-gray-50"
                        />
                      </div>
                      <p className="text-xs text-olive mt-1">E-Mail kann nicht geändert werden</p>
                    </div>

                    <div>
                      <Label htmlFor="displayName" className="text-navy">Name</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                        <Input
                          id="displayName"
                          type="text"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          placeholder="Dein Name"
                          className="pl-10 py-5 rounded-xl"
                          data-testid="profile-name-input"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="bg-terracotta hover:bg-terracotta/90 text-white rounded-xl px-6 gap-2"
                      disabled={loading}
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4" />
                      {loading ? 'Speichern...' : 'Speichern'}
                    </Button>
                  </form>
                </>
              )}

              {activeTab === 'security' && (
                <>
                  <h2 className="font-display text-xl font-semibold text-navy mb-6">
                    Passwort ändern
                  </h2>
                  
                  <form onSubmit={handleUpdatePassword} className="space-y-5">
                    <div>
                      <Label htmlFor="newPassword" className="text-navy">Neues Passwort</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                        <Input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Mindestens 6 Zeichen"
                          className="pl-10 py-5 rounded-xl"
                          data-testid="new-password-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword" className="text-navy">Passwort bestätigen</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Passwort wiederholen"
                          className="pl-10 py-5 rounded-xl"
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="bg-terracotta hover:bg-terracotta/90 text-white rounded-xl px-6 gap-2"
                      disabled={loading || !newPassword || !confirmPassword}
                      data-testid="change-password-btn"
                    >
                      <Lock className="w-4 h-4" />
                      {loading ? 'Speichern...' : 'Passwort ändern'}
                    </Button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
